import {
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  FormField,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {usePutServiceOrderItemsWorkTypeChangeMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, useWorkTypeOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  bulkWorkTypeChangeFormSchema,
  BulkWorkTypeChangeFormType,
} from '../types/bulkWorkTypeChangeFormSchema';

interface BulkWorkTypeChangeFormProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  items: {serviceJobId: string; serviceItemId: string}[];
  onSubmit: VoidFunction;
  onClose: VoidFunction;
}

export function BulkWorkTypeChangeForm(props: BulkWorkTypeChangeFormProps) {
  const {isLoading, isError, getOptionsWithSelectedValue, defaultWorkTypeId} = useWorkTypeOptions();

  const [putServiceOrderItemsWorkTypeChange] = usePutServiceOrderItemsWorkTypeChangeMutation();

  const handleSubmit: FormSubmitHandler<BulkWorkTypeChangeFormType> = async (data) =>
    await putServiceOrderItemsWorkTypeChange({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        serviceItems: props.items.map((item) => ({
          serviceItemId: item.serviceItemId,
          serviceJobId: item.serviceJobId,
        })),
        workTypeId: data.workTypeId,
      },
    })
      .unwrap()
      .then(() => {
        props.onSubmit();
        props.onClose();
      })
      .catch(handleApiError);

  const defaultValues: Partial<BulkWorkTypeChangeFormType> = {
    workTypeId: defaultWorkTypeId ?? undefined,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={34}>
      <Form<BulkWorkTypeChangeFormType>
        experimentalZodSchema={bulkWorkTypeChangeFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              isRequired
              isNotClearable
              control={control}
              label={i18n.t('entity.order.labels.workType')}
              name="workTypeId"
              type="choice"
              options={getOptionsWithSelectedValue(defaultWorkTypeId)}
              data-testid={suffixTestId('workType', props)}
              menuInPortal
            />
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('discardButton', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.confirm')}
                data-testid={suffixTestId('confirmButton', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
